import React from 'react'
import { Box, Typography, LinearProgress, alpha, Tooltip, Chip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { AggregatedMetrics, AnalyticsBenchmark } from '../../hooks/useAnalytics'

interface BenchmarkComparisonProps {
  metrics: AggregatedMetrics
  benchmark?: AnalyticsBenchmark | null
  days: number
  title?: string
}

interface ComparisonRow {
  label: string
  value: number
  benchmark: number
  percentile50: number
  percentile75: number
  percentile90: number
}

export default function BenchmarkComparison({
  metrics,
  benchmark,
  days,
  title = 'Sammenligning med andre',
}: BenchmarkComparisonProps) {
  // If no benchmark data, show placeholder
  if (!benchmark) {
    return (
      <Box>
        {title && (
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography color="text.secondary" variant="body2">
            Benchmark data er ikke tilgængelig endnu. Vi indsamler data for at kunne sammenligne din performance med andre bedemænd i regionen.
          </Typography>
        </Box>
      </Box>
    )
  }

  // Calculate daily averages from the selected period
  const dailyAvgViews = days > 0 ? metrics.total_profile_views / days : 0
  const dailyAvgPhoneReveals = days > 0 ? metrics.total_phone_reveals / days : 0

  const comparisons: ComparisonRow[] = [
    {
      label: 'Daglige visninger',
      value: dailyAvgViews,
      benchmark: benchmark.avg_profile_views || 0,
      percentile50: benchmark.p50_profile_views || 0,
      percentile75: benchmark.p75_profile_views || 0,
      percentile90: benchmark.p90_profile_views || 0,
    },
    {
      label: 'Daglige telefon-klik',
      value: dailyAvgPhoneReveals,
      benchmark: benchmark.avg_phone_reveals || 0,
      percentile50: benchmark.p50_phone_reveals || 0,
      percentile75: benchmark.p75_phone_reveals || 0,
      percentile90: benchmark.p90_phone_reveals || 0,
    },
  ]

  const getPercentileLabel = (value: number, p50: number, p75: number, p90: number) => {
    if (value >= p90) return { label: 'Top 10%', color: '#16a34a' }
    if (value >= p75) return { label: 'Top 25%', color: '#0284c7' }
    if (value >= p50) return { label: 'Over middel', color: '#d97706' }
    return { label: 'Under middel', color: '#6b7280' }
  }

  const getDifferenceText = (value: number, benchmark: number) => {
    if (benchmark === 0) return null
    const diff = ((value - benchmark) / benchmark) * 100
    return {
      value: diff,
      isPositive: diff >= 0,
    }
  }

  return (
    <Box>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
          <Tooltip title="Sammenligning med gennemsnittet af andre bedemænd i samme region">
            <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {comparisons.map((row) => {
          const percentileInfo = getPercentileLabel(row.value, row.percentile50, row.percentile75, row.percentile90)
          const diff = getDifferenceText(row.value, row.benchmark)
          const progressPercent = row.percentile90 > 0 
            ? Math.min((row.value / row.percentile90) * 100, 100) 
            : 0

          return (
            <Box key={row.label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {row.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={percentileInfo.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: alpha(percentileInfo.color, 0.1),
                      color: percentileInfo.color,
                    }}
                  />
                  {diff && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        color: diff.isPositive ? 'success.main' : 'error.main',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {diff.isPositive ? (
                        <TrendingUpIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <TrendingDownIcon sx={{ fontSize: 14 }} />
                      )}
                      {diff.isPositive ? '+' : ''}{diff.value.toFixed(0)}%
                    </Box>
                  )}
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: alpha('#5c4b99', 0.1),
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        backgroundColor: percentileInfo.color,
                      },
                    }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50, textAlign: 'right' }}>
                  {row.value.toFixed(1)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Gennemsnit: {row.benchmark.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top 10%: {row.percentile90.toFixed(1)}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
